import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { cn } from "@/lib/util";
import { X, Palette, Upload, Trash2, Save, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export function ClientBrandingModal({ clientId, clientName, open, onClose }) {
  const [state, setState] = useState(null);
  const [firmName, setFirmName] = useState("");
  const [subline, setSubline] = useState("");
  const [primary, setPrimary] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [logoB64, setLogoB64] = useState(null);
  const [logoMime, setLogoMime] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [nonce, setNonce] = useState(0);

  const load = async () => {
    if (!clientId) return;
    const r = await api.get(`/clients/${clientId}/branding`);
    setState(r.data);
    const o = r.data.override || {};
    setFirmName(o.firm_name || "");
    setSubline(o.subline || "");
    setPrimary(o.primary_color || "");
    setDisclaimer(o.footer_disclaimer || "");
    setLogoB64(null); setLogoMime(null); setLogoPreview(null);
    setNonce(n => n + 1);
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, clientId]);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 512 * 1024) { toast.error("Logo must be < 512KB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
      setLogoB64(reader.result.split(",")[1]);
      setLogoMime(f.type);
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        firm_name: firmName || null,
        subline: subline || null,
        primary_color: primary || null,
        footer_disclaimer: disclaimer || null,
      };
      if (logoB64) { payload.logo_b64 = logoB64; payload.logo_mime = logoMime; }
      await api.put(`/clients/${clientId}/branding`, payload);
      toast.success("Client branding saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const clearLogo = async () => {
    await api.put(`/clients/${clientId}/branding`, { clear_logo: true });
    toast.success("Logo removed"); load();
  };

  const clearAll = async () => {
    if (!window.confirm("Remove all client-specific branding overrides?")) return;
    setClearingAll(true);
    try {
      await api.put(`/clients/${clientId}/branding`, { clear_all: true });
      toast.success("Overrides cleared — client will use tenant defaults");
      load();
    } finally { setClearingAll(false); }
  };

  if (!open) return null;
  if (!state) {
    return (
      <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const eff = state.effective || {};
  const ov = state.override || {};
  const clientLogoUrl = ov.logo_set ? `${API_BASE}/clients/${clientId}/branding/logo?n=${nonce}` : null;

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl bg-card border border-border max-h-[92vh] overflow-y-auto"
           data-testid="client-branding-modal">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="overline flex items-center gap-2"><Palette className="w-3 h-3 text-primary" /> Client branding override</div>
            <h2 className="font-display text-2xl font-medium mt-1">{clientName}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Any field left blank inherits from your tenant branding. Perfect for multi-brand consultancies shipping personalized reports.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Effective preview */}
          <div className="border border-border bg-background p-3">
            <div className="overline mb-2">Effective (what appears on this client's PDFs)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-[11px]">
              <EffField label="Firm" value={eff.firm_name || "—"} inherited={!ov.firm_name} />
              <EffField label="Color" value={eff.primary_color || "—"} inherited={!ov.primary_color} color={eff.primary_color} />
              <EffField label="Subline" value={eff.subline || "—"} inherited={!ov.subline} />
              <EffField label="Logo" value={eff.logo_set ? "set" : "—"} inherited={!ov.logo_set} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={`Firm name ${!ov.firm_name && eff.firm_name ? `(inherits: ${eff.firm_name})` : ""}`}>
              <input value={firmName} onChange={(e) => setFirmName(e.target.value)} maxLength={40}
                     placeholder="Leave blank to inherit" data-testid="cb-firm-name"
                     className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
            </Field>
            <Field label={`Subline ${!ov.subline && eff.subline ? `(inherits: ${eff.subline})` : ""}`}>
              <input value={subline} onChange={(e) => setSubline(e.target.value)} maxLength={80}
                     placeholder="Leave blank to inherit" data-testid="cb-subline"
                     className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
            </Field>
            <Field label={`Primary color ${!ov.primary_color && eff.primary_color ? `(inherits: ${eff.primary_color})` : ""}`}>
              <div className="flex items-center gap-2">
                <input type="color" value={primary || "#3B82F6"} onChange={(e) => setPrimary(e.target.value)} data-testid="cb-color-picker"
                       className="w-12 h-9 bg-background border border-border cursor-pointer" />
                <input value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="#RRGGBB or blank"
                       data-testid="cb-color-hex"
                       className="flex-1 bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
                {primary && (
                  <button onClick={() => setPrimary("")} className="border border-border p-2 hover:border-sev-critical" title="Clear color" data-testid="cb-color-clear">
                    <RefreshCw className="w-3 h-3" />
                  </button>
                )}
              </div>
            </Field>
            <Field label="Logo (PNG/JPG, ≤ 512KB)">
              <div className="flex items-center gap-2">
                <label className="border border-border px-3 py-2 hover:bg-muted cursor-pointer inline-flex items-center gap-2 text-xs font-mono flex-1"
                       data-testid="cb-logo-upload">
                  <Upload className="w-3 h-3" />
                  <span className="truncate">{logoPreview ? "Change file" : (ov.logo_set ? "Replace" : "Choose file")}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
                </label>
                {(logoPreview || clientLogoUrl) && (
                  <div className="w-14 h-9 border border-border bg-background flex items-center justify-center overflow-hidden">
                    <img src={logoPreview || clientLogoUrl} alt="logo" className="max-w-full max-h-full" />
                  </div>
                )}
                {ov.logo_set && (
                  <button onClick={clearLogo} className="border border-border p-2 hover:border-sev-critical hover:text-sev-critical" data-testid="cb-logo-clear" title="Remove client logo">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </Field>
          </div>

          <Field label={`Footer disclaimer ${!ov.footer_disclaimer && eff.footer_disclaimer ? "(inherits tenant)" : ""}`}>
            <textarea value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} rows={3} maxLength={1000}
                      placeholder="Leave blank to inherit tenant disclaimer" data-testid="cb-disclaimer"
                      className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none resize-none" />
          </Field>
        </div>

        <div className="p-5 border-t border-border flex justify-between items-center">
          <button onClick={clearAll} disabled={clearingAll}
                  className="border border-border text-xs tracking-widest uppercase px-3 py-2 hover:border-sev-critical hover:text-sev-critical inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="cb-clear-all">
            {clearingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Reset to tenant defaults
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="border border-border px-4 py-2 text-sm hover:bg-muted">Close</button>
            <button onClick={save} disabled={saving} data-testid="cb-save"
                    className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save overrides
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}

function EffField({ label, value, inherited, color }) {
  return (
    <div>
      <div className="text-muted-foreground uppercase tracking-widest text-[9px]">{label}</div>
      <div className="flex items-center gap-2 mt-0.5">
        {color && <span className="w-3 h-3 border border-border" style={{ backgroundColor: color }} />}
        <span className="truncate">{value}</span>
        {inherited && <span className="text-[9px] px-1 py-0.5 border border-border text-muted-foreground uppercase tracking-widest">tenant</span>}
      </div>
    </div>
  );
}
